import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Boxes, Building2, ChevronLeft, ChevronRight, FileDown, FileUp, FolderTree, Globe2, Inbox, LayoutDashboard, LayoutGrid, Leaf, LogOut, Package, Ruler, ScrollText, Settings as SettingsIcon, Sprout, Users2, Warehouse,
} from "lucide-react";
import { useAuth } from "../AuthContext";
import { useNurserySettings } from "../SettingsContext";
import { api } from "../api";
import NotificationBell from "./NotificationBell";
import { Toaster } from "./ui/sonner";
import MobileNavSheet from "./MobileNavSheet";

const nav = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/admin/orders", label: "Orders", icon: Package, testid: "nav-orders" },
  { to: "/admin/products", label: "Products", icon: LayoutGrid, testid: "nav-products" },
  { to: "/admin/stock", label: "Stock", icon: Warehouse, testid: "nav-stock" },
  { to: "/admin/stock-logs", label: "Stock log", icon: ScrollText, testid: "nav-stock-logs" },
  { to: "/admin/csv", label: "CSV import", icon: FileUp, testid: "nav-csv" },
  { to: "/admin/availability", label: "Availability", icon: FileDown, testid: "nav-availability" },
  { to: "/admin/outbox", label: "Outbox", icon: Inbox, testid: "nav-outbox" },
  { to: "/admin/categories", label: "Categories", icon: FolderTree, testid: "nav-categories" },
  { to: "/admin/sizes", label: "Sizes", icon: Ruler, testid: "nav-sizes" },
  { to: "/admin/parents", label: "Parents", icon: Sprout, testid: "nav-parents" },
  { to: "/admin/tiers", label: "Tier Templates", icon: Boxes, testid: "nav-tiers" },
  { to: "/admin/countries", label: "Countries", icon: Globe2, testid: "nav-countries" },
  { to: "/admin/buyers", label: "Buyers", icon: Users2, testid: "nav-buyers" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const BrandBlock = ({ compact = false }) => {
  const { nurseryName, logoUrl } = useNurserySettings();
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span
        className="w-8 h-8 rounded-md bg-[#2D6A4F] text-white grid place-items-center shrink-0 overflow-hidden"
        data-testid="admin-brand-logo"
      >
        {logoUrl ? (
          <img src={logoUrl} alt="" className="w-full h-full object-contain" />
        ) : (
          <Leaf size={18} />
        )}
      </span>
      {!compact && (
        <div className="db-h min-w-0">
          <div className="text-sm font-bold leading-tight truncate" data-testid="admin-brand-name">
            {nurseryName}
          </div>
          <div className="text-xs text-[#5C665A] leading-tight truncate">Wholesale Admin</div>
        </div>
      )}
    </div>
  );
};

const UserFooter = ({ user, onLogout }) => (
  <>
    <div className="flex items-center gap-2 text-xs text-[#5C665A] mb-2">
      <Building2 size={14} />
      <span className="truncate" data-testid="sidebar-user-email">{user?.email}</span>
    </div>
    <button
      data-testid="logout-btn"
      onClick={onLogout}
      className="w-full flex items-center gap-2 justify-center min-h-[44px] rounded-md border border-[#E2E4E0] text-sm text-[#1A1D1A] hover:bg-[#F9F9F8] transition-colors active:scale-95"
    >
      <LogOut size={14} /> Log out
    </button>
  </>
);

const STORAGE_KEY = "db_admin_sidebar_collapsed";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = () => { logout(); navigate("/login"); };

  const [pendingCount, setPendingCount] = useState(0);
  const refreshPending = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/orders?status=pending");
      setPendingCount(Array.isArray(data) ? data.length : 0);
    } catch { /* silent */ }
  }, []);
  useEffect(() => {
    refreshPending();
    const i = setInterval(refreshPending, 30000);
    return () => clearInterval(i);
  }, [refreshPending]);

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  const sidebarWidth = collapsed ? "w-14" : "w-60";

  return (
    <div className="min-h-screen flex" data-testid="admin-layout">
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex ${sidebarWidth} shrink-0 bg-white border-r border-[#E2E4E0] flex-col transition-[width] duration-200`}
             data-testid="admin-sidebar" data-collapsed={collapsed ? "true" : "false"}>
        <div className={`flex items-center ${collapsed ? "justify-center" : "justify-between"} gap-2 px-2 h-14 border-b border-[#E2E4E0]`}>
          {!collapsed && (
            <Link to="/admin/products" className="flex items-center gap-2 min-w-0" data-testid="sidebar-brand">
              <BrandBlock />
            </Link>
          )}
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            data-testid="admin-sidebar-toggle"
            className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-[#E2E4E0] bg-white text-[#5C665A] hover:bg-[#F9F9F8] hover:text-[#1A1D1A] active:scale-95 transition"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                `flex items-center ${collapsed ? "justify-center px-0" : "px-3"} gap-2 h-9 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-[#E9F5EC] text-[#1B4332] font-medium"
                    : "text-[#1A1D1A] hover:bg-[#F9F9F8]"
                }`
              }
            >
              <item.icon size={16} strokeWidth={2} />
              {!collapsed && <span className="truncate flex-1">{item.label}</span>}
              {item.to === "/admin/orders" && pendingCount > 0 && (
                <span
                  className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${collapsed ? "absolute translate-x-3 -translate-y-2 bg-[#C94A4A] text-white" : "bg-[#C94A4A] text-white"}`}
                  data-testid="nav-orders-pending-badge"
                >
                  {pendingCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className={`border-t border-[#E2E4E0] ${collapsed ? "p-2" : "px-3 py-3"}`}>
          {collapsed ? (
            <button
              data-testid="logout-btn-collapsed"
              onClick={doLogout}
              title={`Log out ${user?.email || ""}`}
              className="w-full inline-flex items-center justify-center h-10 rounded-md border border-[#E2E4E0] text-[#5C665A] hover:text-[#C94A4A] hover:bg-[#FDF3F3] active:scale-95 transition"
              aria-label="Log out"
            >
              <LogOut size={16} />
            </button>
          ) : (
            <UserFooter user={user} onLogout={doLogout} />
          )}
        </div>
      </aside>

      {/* Main + mobile top bar */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Desktop top-right action bar — notifications bell. */}
        <div className="hidden md:flex sticky top-0 z-20 h-14 bg-white/80 backdrop-blur border-b border-[#E2E4E0] px-4 items-center justify-end gap-3" data-testid="admin-topbar">
          <NotificationBell />
        </div>
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-30 h-14 bg-white border-b border-[#E2E4E0] px-3 flex items-center gap-2">
          <MobileNavSheet
            items={nav}
            header={<BrandBlock />}
            footer={<UserFooter user={user} onLogout={doLogout} />}
            triggerTestid="admin-mobile-nav-trigger"
          />
          <Link to="/admin/products" className="flex items-center gap-2 flex-1" data-testid="admin-mobile-brand">
            <BrandBlock />
          </Link>
          <NotificationBell />
        </div>
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </div>

      <Toaster position="bottom-right" richColors closeButton />
    </div>
  );
}
